import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Switch,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IconAlertCircle } from "@/lib/icons";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import {
  type Mkb10Read,
  createMkb10,
  updateMkb10,
} from "@/lib/api/references";

interface Props {
  opened: boolean;
  onClose: () => void;
  initial?: Mkb10Read | null;
}

export function Mkb10FormDialog({ opened, onClose, initial }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = !!initial;

  const form = useForm({
    initialValues: {
      code: initial?.code ?? "",
      description_ru: initial?.description_ru ?? "",
      description_uz: initial?.description_uz ?? "",
      description_en: initial?.description_en ?? "",
      parent_code: initial?.parent_code ?? "",
      is_billable: initial?.is_billable ?? true,
    },
    validate: {
      code: (v) =>
        isEdit
          ? null
          : /^[A-Z][0-9]{2}(\.[0-9]{1,2})?$/.test(v.trim())
            ? null
            : t("references.mkb_validation.code"),
      description_ru: (v) =>
        v.trim().length > 0 ? null : t("validation.required"),
    },
  });

  // Re-populate form when opening with a different item
  useEffect(() => {
    if (opened) {
      form.setValues({
        code: initial?.code ?? "",
        description_ru: initial?.description_ru ?? "",
        description_uz: initial?.description_uz ?? "",
        description_en: initial?.description_en ?? "",
        parent_code: initial?.parent_code ?? "",
        is_billable: initial?.is_billable ?? true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, initial?.code]);

  const mutation = useMutation({
    mutationFn: async (values: typeof form.values) => {
      if (isEdit) {
        return updateMkb10(initial!.code, {
          description_ru: values.description_ru.trim(),
          description_uz: values.description_uz.trim() || null,
          description_en: values.description_en.trim() || null,
          parent_code: values.parent_code.trim() || null,
          is_billable: values.is_billable,
        });
      }
      return createMkb10({
        code: values.code.trim().toUpperCase(),
        description_ru: values.description_ru.trim(),
        description_uz: values.description_uz.trim() || null,
        description_en: values.description_en.trim() || null,
        parent_code: values.parent_code.trim() || null,
        is_billable: values.is_billable,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mkb10"] });
      queryClient.invalidateQueries({ queryKey: ["mkb10-page"] });
      notifications.show({
        color: "green",
        message: isEdit ? t("references.mkb_updated") : t("references.mkb_created"),
      });
      form.reset();
      onClose();
    },
  });

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) {
      if (mutation.error.code === "conflict") return t("references.mkb_duplicate");
      return mutation.error.message;
    }
    return t("auth.network_error");
  })();

  return (
    <Modal
      opened={opened}
      onClose={() => {
        form.reset();
        onClose();
      }}
      title={isEdit ? t("references.mkb_edit") : t("references.mkb_create")}
      size="lg"
      radius="lg"
    >
      {errorMessage && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} mb="md" radius="lg">
          {errorMessage}
        </Alert>
      )}
      <form onSubmit={form.onSubmit((v) => mutation.mutate(v))}>
        <Stack>
          <TextInput
            label={t("references.mkb10_fields.code")}
            placeholder="I10"
            required
            disabled={isEdit}
            {...form.getInputProps("code")}
          />
          <Textarea
            label={t("references.mkb_fields.description_ru")}
            placeholder="Эссенциальная гипертензия"
            required
            minRows={2}
            autosize
            {...form.getInputProps("description_ru")}
          />
          <Textarea
            label={t("references.mkb_fields.description_uz")}
            placeholder="Birlamchi gipertenziya"
            minRows={2}
            autosize
            {...form.getInputProps("description_uz")}
          />
          <Textarea
            label={t("references.mkb_fields.description_en")}
            placeholder="Essential hypertension"
            minRows={2}
            autosize
            {...form.getInputProps("description_en")}
          />
          <TextInput
            label={t("references.mkb_fields.parent")}
            placeholder="I10-I15"
            {...form.getInputProps("parent_code")}
          />
          <Switch
            label={t("references.mkb_fields.is_billable")}
            description={t("references.mkb_fields.is_billable_hint")}
            {...form.getInputProps("is_billable", { type: "checkbox" })}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={mutation.isPending} radius="xl">
              {isEdit ? t("common.save") : t("common.create")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
